import { FC, useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { IonButton } from '@ionic/react';
import { useHistory } from 'react-router';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { CareMapState } from '../../caremap/CareMapState';
import { CommunityModelFactory } from '../CommunityModelFactory';
import { RevenueCatModel } from '../../innerhive-plus/revenue-cat/RevenueCatModel';
import { InnerhivePlusDialogViewModel } from '../../innerhive-plus/InnerhivePlusDialogViewModel';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { AlertPresenter } from '../../AlertBinder';
import { Logger } from '../../support/Logger';
import { RelationshipSelectField } from '../../components/RelationshipSelectField';
import { HeaderPopover } from '../../components/HeaderPopover';
import { Text } from '../../components/Text';
import { TextField } from '../../components/TextField';
import { TabRadioButtons } from '../../components/TabRadioButtons';
import { CheckIcon, InfoIcon } from '../../theme/Icons';
import { IRelationship, ICaremapAccess } from '../../caremap/ICareMap';
import { AddFromContactsButton, IAddFromContactsButtonResult } from '../../contacts/AddFromContactsButton';

// Guided Add Person flow for Sharing & Access: invite (name/email) → access (with the
// live co-owner quota + paywall) → optionally save contact → relationship → review →
// "invite sent" success. Submit creates the invite (sends the SendGrid email) and, when
// saving, categorizes the contact and stamps the member link.

const useStyles = createUseStyles({
    infoHeadingRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 2
    },
    careMapPopoverImg: {
        display: 'block',
        width: '100%',
        maxWidth: 240,
        height: 'auto',
        margin: '0 auto 12px',
        borderRadius: 8
    },
    field: {
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        marginTop: 12
    },
    accessGrid: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        marginTop: 8
    },
    accessCard: {
        border: '1px solid var(--ion-color-step-150)',
        borderRadius: 10,
        padding: '12px 12px 14px',
        background: '#fff',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 5
    },
    accessCardOn: {
        borderColor: 'var(--ion-color-primary)',
        background: 'rgba(17,120,157,0.06)',
        boxShadow: '0 0 0 1px var(--ion-color-primary)'
    },
    quotaBadge: {
        display: 'inline-block',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        padding: '2px 6px',
        borderRadius: 4,
        background: 'rgba(146,61,172,0.12)',
        color: 'var(--ion-color-tertiary)',
        alignSelf: 'flex-start'
    },
    quotaBadgeUpgrade: {
        background: 'rgba(238,108,78,0.12)',
        color: 'var(--ion-color-secondary)'
    },
    hintBanner: {
        marginTop: 12,
        padding: '10px 12px',
        borderRadius: 8,
        background: 'rgba(238,108,78,0.08)',
        border: '1px solid rgba(238,108,78,0.2)'
    },
    review: {
        marginTop: 16,
        padding: 14,
        background: 'var(--ion-color-step-50)',
        borderRadius: 10
    },
    successWrap: {
        marginTop: 24,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 8
    },
    successCheck: {
        width: 56,
        height: 56,
        borderRadius: '50%',
        background: 'rgba(17,120,157,0.1)',
        color: 'var(--ion-color-primary)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 28
    },
    flowWrap: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
    },
    flowBody: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        padding: '20px 22px 16px'
    },
    // Pinned action footer: a sibling of the scroll body (not sticky-inside it) so a step's
    // controls — e.g. the save-contact toggle's checkboxes — never bleed over the buttons.
    flowFooter: {
        flexShrink: 0,
        display: 'flex',
        gap: 10,
        background: '#fff',
        padding: '12px 22px',
        borderTop: '1px solid var(--ion-color-step-100)'
    }
});

const isValidEmail = (s: string): boolean => /\S+@\S+\.\S+/.test(s);

type IStepNum = 1 | 2 | 3 | 4 | 5 | 6;

export interface IAddPersonFlowProps {
    onCancel: () => void;
    onDone: () => void;
    /** Fully closes the Sharing & Access sheet (used before navigating into a care-map drawer). */
    onClose: () => void;
    patientFirstName: string;
}

export const AddPersonFlow: FC<IAddPersonFlowProps> = ({ onCancel, onDone, onClose, patientFirstName }) => {
    const classes = useStyles();
    const history = useHistory();

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);
    const communityFactory = useInstance(CommunityModelFactory);
    useSubscription(() => communityFactory);
    const community = communityFactory.forActiveCaremap();
    const revenueCat = useInstance(RevenueCatModel);
    const paywall = useInstance(InnerhivePlusDialogViewModel);
    const tracking = useInstance(MixPanelEventEmitter);
    const alert = useInstance(AlertPresenter);
    const logger = useInstance(Logger);

    const [step, setStep] = useState<IStepNum>(1);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [access, setAccess] = useState<ICaremapAccess | undefined>(undefined);
    const [saveInfo, setSaveInfo] = useState<boolean | undefined>(undefined);
    const [relationshipLabel, setRelationshipLabel] = useState<string | undefined>(undefined);
    const [relationshipId, setRelationshipId] = useState<IRelationship | undefined>(undefined);
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState<{ placement: 'inner-circle' | 'community' | 'none'; connectedToExisting: boolean; memberId?: string } | undefined>(undefined);
    const [showCareMapInfo, setShowCareMapInfo] = useState(false);
    // Whether the invitee's name/email were seeded from the device contact picker (native only).
    const [usedContacts, setUsedContacts] = useState(false);

    useEffect(() => {
        tracking.track('Invite Started');
    }, [tracking]);

    const hasInnerhivePlus = revenueCat.hasInnerhivePlus();
    // getFreeTierLimits throws for Plus users (they have no limits), so only read it without Plus.
    const freeTierLimits = hasInnerhivePlus ? undefined : revenueCat.getFreeTierLimits();
    const freeCoOwnerQuota = freeTierLimits?.freeCoOwnersPerCaremap ?? 0;
    const coOwnersUsed = community.isLoaded() ? community.getCoOwners().length : 0;
    // Reuse the model's encapsulated rule (counts pending invites + accepted co-owners).
    // Plus users have no co-owner limit, so they're never "over quota".
    const overFreeQuota = !!freeTierLimits && community.addCoOwnerRequiresInnerhivePlus(freeTierLimits);

    // The same age-keyed care map illustration the onboarding "Visualize Care" step uses,
    // so the concept reads identically in both places.
    const careMapImage = ((): string => {
        const dob = careMapState.getActiveCaremap()?.patient?.dob;
        if (dob) {
            const ageYears = (Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000);
            if (ageYears <= 25) {
                return '/assets/images/onboarding/caremap-eleanor.png';
            }
            if (ageYears <= 50) {
                return '/assets/images/onboarding/caremap-samantha.png';
            }
        }
        return '/assets/images/onboarding/caremap-arthur.png';
    })();

    const step1Valid = firstName.trim() && lastName.trim() && isValidEmail(email);
    const step2Valid = !!access;
    const step3Valid = saveInfo !== undefined;
    const step4Valid = !!relationshipLabel;

    const name = firstName.trim();
    const possessive = name ? `${name}'s` : 'their';

    // Step 4 (relationship) only appears when saving info; otherwise Next from 3 skips to review.
    const nextStepFrom = (current: IStepNum): IStepNum => {
        if (current === 3 && saveInfo === false) {
            return 5;
        }
        return Math.min(current + 1, 6) as IStepNum;
    };
    const prevStepFrom = (current: IStepNum): IStepNum => {
        if (current === 5 && saveInfo === false) {
            return 3;
        }
        return Math.max(current - 1, 1) as IStepNum;
    };

    const back = () => {
        if (step === 1) {
            onCancel();
        } else {
            setStep(prevStepFrom(step));
        }
    };

    const advance = async () => {
        // Co-owner past the free quota without Innerhive Plus routes through the paywall.
        if (step === 2 && access === 'co-owner' && overFreeQuota) {
            const purchased = await paywall.requirePurchase({ triggerSource: 'co-owner invite' });
            if (!purchased) {
                return;
            }
        }
        tracking.track('Invite Step Completed', () => ({ step, access }));
        setStep(nextStepFrom(step));
    };

    // Soft hint only — a name match never auto-links; email match auto-links at submit.
    const nameMatch = step1Valid && !careMapState.findMemberByEmail(email)
        ? careMapState.findMemberByName({ firstName: firstName.trim(), lastName: lastName.trim() })
        : undefined;

    const submit = async () => {
        if (!access || saveInfo === undefined || sending) {
            return;
        }
        setSending(true);
        try {
            const res = await careMapState.invitePerson({
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                email: email.trim(),
                access,
                saveContact: saveInfo,
                relationshipId,
                relationship: relationshipLabel
            });

            // Refresh so the new pending invite shows in the people list with its delete link.
            await community.load();

            const categoryLabel = res.placement === 'inner-circle' ? 'Inner Circle' : res.placement === 'community' ? 'Community' : 'None';
            tracking.track('Invite Sent', () => ({
                access,
                savedContact: saveInfo,
                connectedToExisting: res.connectedToExisting,
                'Care Map Category': categoryLabel,
                'Used Contacts': usedContacts
            }));
            if (res.memberId) {
                tracking.track('Invite Connected to Existing Care Map Member', () => ({ matchedBy: res.connectedToExisting ? 'email' : 'created' }));
            }

            setResult({ placement: res.placement, connectedToExisting: res.connectedToExisting, memberId: res.memberId });
            setStep(6);
        } catch (err: any) {
            logger.error(err);
            await alert.showAlertV2({
                header: 'Something went wrong',
                message: err?.displayMessage || `We couldn't send the invite. Please try again.`
            });
        } finally {
            setSending(false);
        }
    };

    const openMemberInCareMap = () => {
        const careMapId = careMapState.getActiveCaremap()?.careMapId;
        // Resolve the member's actual hive (it may have linked to an existing member in
        // any support group, not just community) so we open the right edit drawer.
        const link = result?.memberId ? careMapState.maybeGetMemberLink(result.memberId) : undefined;
        if (!careMapId || !result?.memberId || !link) {
            onClose();
            return;
        }
        const path = link.hiveKey === 'household'
            ? `/caremaps/${careMapId}/drawer/household/member/${result.memberId}/edit`
            : `/caremaps/${careMapId}/drawer/support-group/${link.hiveKey}/member/${result.memberId}/edit`;
        // Close the sheet first; navigate once its modal has dismissed, otherwise the
        // drawer mounts under the open modal (and HouseholdRouter's useDismissOverlays
        // races the route change), leaving the caremap context briefly unavailable.
        onClose();
        setTimeout(() => history.push(path), 350);
    };

    const handleContactSelected = (patch: IAddFromContactsButtonResult) => {
        setFirstName(patch.firstName || '');
        setLastName(patch.lastName || '');
        setEmail(patch.contactMethods.find(cm => cm.type === 'email')?.value || '');
        setUsedContacts(true);
    };

    return (
        <div className={classes.flowWrap}>
            <div className={classes.flowBody}>
                {step === 1 && (
                    <>
                        <Text type="title">Who are you inviting?</Text>
                        <Text type="detail">We'll send their invite by email.</Text>
                        <AddFromContactsButton onContactSelected={handleContactSelected} />
                        <div className={classes.field}>
                            <TextField data-id="add-person-first-name" label="First name" value={firstName} autoFocus autocapitalize="words" onInput={setFirstName} />
                        </div>
                        <div className={classes.field}>
                            <TextField data-id="add-person-last-name" label="Last name" value={lastName} autocapitalize="words" onInput={setLastName} />
                        </div>
                        <div className={classes.field}>
                            <TextField data-id="add-person-email" label="Email" value={email} onInput={setEmail} />
                        </div>
                    </>
                )}

                {step === 2 && (
                    <>
                        <Text type="title">What can {firstName.trim() || 'they'} see and do?</Text>
                        <Text type="detail">You can change this later.</Text>
                        <div className={classes.accessGrid}>
                            <div data-type="access-view-only" className={`${classes.accessCard} ${access === 'view-only' ? classes.accessCardOn : ''}`} onClick={() => setAccess('view-only')}>
                                <Text type="subtitle">Viewer</Text>
                                <Text type="detail">Sees shared notes, recordings, and appointments. Can comment but can't edit or see hidden info.</Text>
                            </div>
                            <div data-type="access-co-owner" className={`${classes.accessCard} ${access === 'co-owner' ? classes.accessCardOn : ''}`} onClick={() => setAccess('co-owner')}>
                                <Text type="subtitle">Co-Owner</Text>
                                <Text type="detail">Helps manage notes, recordings, appointments, and tasks alongside you. Sees everything, including hidden info.</Text>
                                {hasInnerhivePlus ? (
                                    <span className={classes.quotaBadge}>Included with Innerhive Plus</span>
                                ) : (
                                    <span className={`${classes.quotaBadge} ${overFreeQuota ? classes.quotaBadgeUpgrade : ''}`}>
                                        {overFreeQuota ? 'Upgrade to invite' : `${freeCoOwnerQuota - coOwnersUsed} free remaining`}
                                    </span>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {step === 3 && (
                    <>
                        <div className={classes.infoHeadingRow}>
                            <Text type="title">Also save {possessive} contact info?</Text>
                            <IonButton id="care-map-info-trigger" fill="clear" color="medium" size="small" onClick={() => setShowCareMapInfo(v => {
                                if (!v) {
                                    tracking.track('Tooltip Opened', () => ({ Tooltip: 'What is a Care Map' }));
                                }
                                return !v;
                            })} aria-label="What's a care map?"><InfoIcon /></IonButton>
                        </div>
                        <Text type="detail">
                            Adds {name || 'them'} to {patientFirstName}'s care map, so everyone helping out can see who else is involved, with contact info a tap away.
                        </Text>
                        {nameMatch && (
                            <div className={classes.hintBanner}>
                                <Text type="detail">
                                    Looks like <strong>{nameMatch.name}</strong> may already be on {patientFirstName}'s care map. Saving will add a separate contact.
                                </Text>
                            </div>
                        )}
                        <TabRadioButtons
                            value={saveInfo}
                            onChange={setSaveInfo}
                            dataTypePrefix="save-contact"
                            buttons={[
                                { label: 'Yes, Add Them', value: true },
                                { label: 'Not Now', value: false }
                            ]}
                        />
                    </>
                )}

                {step === 4 && (
                    <>
                        <Text type="title">Relationship to {patientFirstName}</Text>
                        <Text type="detail">How is {firstName.trim() || 'this person'} related to {patientFirstName}?</Text>
                        <div className={classes.field}>
                            <RelationshipSelectField
                                label={`Relationship to ${patientFirstName}`}
                                required
                                relationshipId={relationshipId}
                                relationship={relationshipLabel}
                                onChange={({ relationshipId: id, relationship: label }) => {
                                    setRelationshipId(id);
                                    setRelationshipLabel(label);
                                }}
                            />
                        </div>
                    </>
                )}

                {step === 5 && (
                    <>
                        <Text type="title">Ready to Send?</Text>
                        <Text type="detail">We'll email {name || 'them'} an invite.</Text>
                        <div className={classes.review}>
                            <Text type="subdetail">Person</Text>
                            <Text type="detail"><span className="mp-mask">{firstName} {lastName} · {email}</span></Text>
                            <Text type="subdetail">Access</Text>
                            <Text type="detail">{access === 'co-owner' ? 'Co-Owner — full access' : 'Viewer — can see shared notes & comment'}</Text>
                            <Text type="subdetail">Care map</Text>
                            <Text type="detail">{saveInfo && relationshipLabel ? `${relationshipLabel} of ${patientFirstName}` : 'Not added to care map'}</Text>
                        </div>
                    </>
                )}

                {step === 6 && result && (
                    <div className={classes.successWrap}>
                        <div className={classes.successCheck}><CheckIcon /></div>
                        <Text type="title">Invite sent</Text>
                        <Text type="detail">
                            We emailed {name || 'them'} an invite to {access === 'co-owner' ? 'co-own' : 'view'} {patientFirstName}'s care.
                            {result.connectedToExisting
                                ? ` They're already on ${patientFirstName}'s care map.`
                                : result.placement === 'inner-circle'
                                    ? ` We added them to ${patientFirstName}'s Inner Circle.`
                                    : result.placement === 'community'
                                        ? ` We added them to ${patientFirstName}'s Community.`
                                        : ''}
                        </Text>
                    </div>
                )}
            </div>

            <div className={classes.flowFooter}>
                {step === 1 && (
                    <>
                        <IonButton data-type="add-person-cancel" fill="outline" expand="block" style={{ flex: 1 }} onClick={onCancel}>Cancel</IonButton>
                        <IonButton data-type="add-person-next" expand="block" style={{ flex: 2 }} onClick={advance} disabled={!step1Valid}>Next</IonButton>
                    </>
                )}
                {step === 2 && (
                    <>
                        <IonButton data-type="add-person-back" fill="outline" expand="block" style={{ flex: 1 }} onClick={back}>Back</IonButton>
                        <IonButton data-type="add-person-next" expand="block" style={{ flex: 2 }} onClick={advance} disabled={!step2Valid}>Next</IonButton>
                    </>
                )}
                {step === 3 && (
                    <>
                        <IonButton data-type="add-person-back" fill="outline" expand="block" style={{ flex: 1 }} onClick={back}>Back</IonButton>
                        <IonButton data-type="add-person-next" expand="block" style={{ flex: 2 }} onClick={advance} disabled={!step3Valid}>Next</IonButton>
                    </>
                )}
                {step === 4 && (
                    <>
                        <IonButton data-type="add-person-back" fill="outline" expand="block" style={{ flex: 1 }} onClick={back}>Back</IonButton>
                        <IonButton data-type="add-person-next" expand="block" style={{ flex: 2 }} onClick={advance} disabled={!step4Valid}>Next</IonButton>
                    </>
                )}
                {step === 5 && (
                    <>
                        <IonButton data-type="add-person-back" fill="outline" expand="block" style={{ flex: 1 }} onClick={back}>Back</IonButton>
                        <IonButton data-type="add-person-send-invite" expand="block" style={{ flex: 2 }} onClick={submit} disabled={sending}>{sending ? 'Sending…' : 'Send Invite'}</IonButton>
                    </>
                )}
                {step === 6 && result && (
                    result.placement !== 'none' && result.memberId ? (
                        <>
                            <IonButton data-type="add-person-done" fill="outline" expand="block" style={{ flex: 1 }} onClick={onDone}>Done</IonButton>
                            <IonButton data-type="add-person-add-details" expand="block" style={{ flex: 1 }} onClick={openMemberInCareMap}>Add Their Details</IonButton>
                        </>
                    ) : (
                        <IonButton data-type="add-person-done" expand="block" style={{ flex: 1 }} onClick={onDone}>Done</IonButton>
                    )
                )}
            </div>

            <HeaderPopover
                trigger="care-map-info-trigger"
                isOpen={showCareMapInfo}
                onDidDismiss={() => setShowCareMapInfo(false)}
                title="What's a Care Map?"
                content={
                    <Text type="detail">
                        <img className={classes.careMapPopoverImg} src={careMapImage} alt="Care map" />
                        A Care Map is a visual overview of all the people and places involved in {patientFirstName}'s care. In Innerhive, it also serves as an intuitive place to organize and store care team details, including essential contact info — keeping everything easily accessible.
                    </Text>
                }
            />
        </div>
    );
};
